import { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import teamColumnsTemplate from './teamColumnsTemplate';
import EditMemberModal from '../EditMemberModal';
import { TeamMember, TeamPricesData } from 'modules/Types';
import { Caption } from 'components/shareable/Typography';
import { TEAM_PLAN_INCLUDED_SEATS } from 'modules/Const'
import { getSimulationPrices_api } from 'api/organizationApi'
import Colors from 'modules/Colors';
import { makeStyles, Theme } from '@material-ui/core';
import useOrganization from 'Hooks/useOrganization';
import RemoveMemberModal from '../RemoveMemberModal';
import useCurrentBrand from 'Hooks/useCurrentBrand';

interface TeamMemberListProps {
  searchTerm?: string;
}

// NOTE: this renders the actual data list of team members
const TeamMemberList = ({
  searchTerm
}: TeamMemberListProps) => {
  const classes = useStyles();
  const { loadedBrandsDict } = useCurrentBrand();
  const { teamMembers, getSelectedTeamMember, setSelectedTeamMemberId, getOrganizationId } = useOrganization();
  const organizationId = getOrganizationId();

  const selectedTeamMember = getSelectedTeamMember();

  const [teamPrices, setTeamPrices] = useState<TeamPricesData>();

  // NOTE: modal states
  const [isShowEditMemberModal, setIsShowEditMemberModal] = useState(false);
  const [isShowRemoveMemberModal, setIsShowRemoveMemberModal] = useState(false);

  /** Filters team members based on search term matching name or email */
  const filteredTeamMembers = searchTerm && searchTerm.length > 1 ?
    teamMembers.filter(
      (member) =>
        member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    :
    teamMembers;

  useEffect(() => {
    if (teamMembers.length >= TEAM_PLAN_INCLUDED_SEATS) {
      // initStripeTeamPlanCosts();
    }
  }, [teamMembers]);

  const initStripeTeamPlanCosts = async () => {
    const teamPricesResponse = await getSimulationPrices_api(organizationId, {typeSimulation: 'remove-seat' });
    if (teamPricesResponse) setTeamPrices(teamPricesResponse);
  };

  const handleOpenEditMemberModal = (teamMember: TeamMember) => {
    setSelectedTeamMemberId(teamMember.id);
    setIsShowEditMemberModal(true);
  };

  const handleCloseEditMemberModal = () => {
    setSelectedTeamMemberId(null);
    setIsShowEditMemberModal(false);
  };

  const handleOpenRemoveMemberModal = () => {
    setIsShowRemoveMemberModal(true);
  };

  const handleCloseRemoveMemberModal = () => {
    setIsShowRemoveMemberModal(false);
  };

  // if (isLoadingTeamMembers) {
  //   return (
  //     <div className='w-full h-16 flex justify-center items-center' style={{minHeight: 200}}>
  //       <LoadingLottie black height={24} />
  //     </div>
  //   );
  // }

  if (!teamMembers.length) {
    return (
      <div className='w-full h-16 flex justify-center items-center'>
        <Caption color={Colors.greyDark}>No users found.</Caption>
      </div>
    )
  }

  if (!filteredTeamMembers.length && searchTerm && searchTerm?.length > 1) {
    return (
      <div className='w-full h-16 flex justify-center items-center'>
        <Caption color={Colors.greyDark}>No users found for '{searchTerm}'.</Caption>
      </div>
    )
  }

  return (
    <>
      <DataGrid
        autoHeight
        hideFooter
        pageSize={10}
        headerHeight={0}
        rowHeight={80}
        rows={filteredTeamMembers}
        columns={teamColumnsTemplate(handleOpenEditMemberModal, loadedBrandsDict || {})}
        classes={{ root: classes.dataGridRoot }}
      />
      {selectedTeamMember &&
        <EditMemberModal
          className="teamMenuEditMemberModal"
          isOpen={isShowEditMemberModal}
          onClose={handleCloseEditMemberModal}
          onRemoveMember={handleOpenRemoveMemberModal}
        />
      }
      {isShowRemoveMemberModal &&
        <RemoveMemberModal
          className="teamMenuRemoveMemberModal"
          isOpen={isShowRemoveMemberModal}
          onClose={handleCloseRemoveMemberModal}
          teamPrices={teamPrices}
        />
      }
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  tableRow: {
    display: 'flex !important',
    justifyContent: 'space-between',
    width: '100%',
    '& > td': {
      display: 'flex',
      border: 'none !important',
      alignItems: 'center',
    },
  },
  bordered: {
    borderBottom: `1px solid ${Colors.divider}`,
  },
  redIcon: {
    '& path': {
      stroke: Colors.red,
    },
  },
  dataGridRoot: {
    border: "none !important",
    outline: "none !important",
    "& .MuiDataGrid-row:hover": {
      background: "none !important",
    },
    "& .MuiDataGrid-main, .MuiDataGrid-root": {
      border: "none",
    },
    "& .MuiDataGrid-row.Mui-selected": {
      backgroundColor: `${Colors.PAPER_CONTAINER} !important`,
    },
    "& .MuiDataGrid-cell:focus": {
      outline: "none",
      borderBottom: `1px solid ${Colors.divider}`,
    },
    "& .MuiDataGrid-cell, .MuiDataGrid-row": {
      borderBottom: `1px solid ${Colors.divider}`,
      cursor: "pointer",
    },
    "& .MuiDataGrid-row:last-of-type": {
      outline: "none",
      borderBottom: 'none',
    },
    "& .MuiDataGrid-row:first-of-type": {
      outline: "none",
      borderTop: 'none',
    },
    "& .MuiDataGrid-cell:hover": {
      cursor: "default",
    },
    "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
      outline: "none !important",
    },
    '& .MuiDataGrid-columnHeaders': {
      border: 'none !important',
    },
  },
}));

export default TeamMemberList;